import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Building2,
  Check,
  Clock,
  Cpu,
  FileWarning,
  Globe,
  Link2,
  Loader2,
  Lock,
  type LucideIcon,
  Rabbit,
  Server,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { currentExitCountry, ExitCountrySelect } from '@/components/egress/ExitCountryField';
import { AnalysedHere } from '@/components/intel/AnalysedHere';
import { HERO_WIDTH, IntelSearchPanel } from '@/components/intel/IntelSearchPanel';
import type { LaunchOption } from '@/components/LaunchOptionsPanel';
import { LaunchOptionsPanel, launchTargets } from '@/components/LaunchOptionsPanel';
import LinkList from '@/components/LinkList';
import { ALL_ORGS, useActiveOrg } from '@/components/OrgPicker';
import { OSIcon } from '@/components/OSIcon';
import PreviewPane from '@/components/PreviewPane';
import RetrievedPayloads from '@/components/RetrievedPayloads';
import StagedFileTree from '@/components/StagedFileTree';
import { SubmitFileIcon } from '@/components/SubmitFileIcon';
import { Segmented } from '@/components/ui/Segmented';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import { ANALYZER_CATALOG_KEY } from '@/features/urlconsole/useVoltzSandbox';
import type { StagedUpload, SupportedFileTypes } from '@/lib/api';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/classnames';
import { countryName } from '@/lib/countries';
import { SUPPORTED_TYPES_FALLBACK, selectSupportedTypes } from '@/lib/fileTypes';
import { formatBytes } from '@/lib/format';
import { canPreview, previewKindOf } from '@/lib/preview/dispatch';

/** Submit a sample: upload or search, look inside, choose what to detonate.
 *
 *  One route and one page. Three ways IN - a file the analyst has, a URL whose
 *  bytes the server retrieves, and an identifier they only have a name for -
 *  and one way through: all arrive at the same staged upload, the same file
 *  tree and the same options, because everything after "we have the bytes" is
 *  the same operation. A second submit flow for a searched hash would be a
 *  second place for the quota, the size ceiling and the file-type gate to drift.
 *
 *  The upload starts the moment a file is dropped, so the inspection runs while
 *  the analyst is still reading the summary; the options appear underneath
 *  rather than on a second screen, because everything they configure applies to
 *  every file they pick. */

/** How soon the analyst wants the run to begin, and what they will accept to
 *  get it. The values are the API's own `start_mode` vocabulary.
 *
 *  Labels say what happens, not what the platform does internally: an analyst
 *  picking here is choosing a start, not configuring a machine pool. */
const START_CHOICES = [
  { value: 'auto', label: 'Automatic' },
  { value: 'warm', label: 'Warm start' },
  { value: 'cold', label: 'Cold boot' },
] as const;

type StartMode = (typeof START_CHOICES)[number]['value'];

const OS_CHOICES = [
  { value: 'windows11', label: 'Windows 11' },
  { value: 'windows10', label: 'Windows 10' },
] as const;

/** Timeout bounds, in whole minutes. The API accepts 30–2100 seconds; the
 *  slider covers that range in the unit an analyst actually thinks in, so any
 *  duration is reachable rather than only the four a preset list would name. */
const TIMEOUT_MIN_MINUTES = 1;
const TIMEOUT_MAX_MINUTES = 35;

/** The default archive password, and the one every mail-borne sample uses. */
const DEFAULT_ARCHIVE_PASSWORD = 'infected';

/** How often to re-read a staged upload while inspection is running. */
const INSPECTION_POLL_MS = 1500;

/** States that mean the server is still working.
 *
 *  'following' is one of them, and it is different from the others: the tree is
 *  already readable, so the page shows it and keeps polling while the payloads
 *  a document pointed at land beside it. */
const PENDING_STATES = new Set(['uploaded', 'inspecting', 'following']);

/** States where the file tree does not exist yet, so there is nothing to show
 *  but a spinner. */
const UNREADABLE_STATES = new Set(['uploaded', 'inspecting']);

/** The two tabs a sample analysis starts from. Both end at a staged upload,
 *  because both produce bytes to look inside. A URL whose bytes the server
 *  retrieves is a third way in but lives inside the upload tab; a URL to
 *  visit with a watched browser is not a sample and has its own page,
 *  /submit-url. */
const TABS = [
  { value: 'upload', label: 'Upload a file' },
  { value: 'search', label: 'Search an identifier' },
] as const;

type Tab = (typeof TABS)[number]['value'];

/** Why a previewable root entry has no bytes yet. A nested entry never has
 *  bytes at all (inspection stores no content), so the section is not rendered
 *  for one rather than rendered around an apology. A root entry without a
 *  local File arrived server-side - fetched from a URL, staged from a prior
 *  run, or adopted from a search - and no single origin story fits all three. */
function previewUnavailable(file: File | null): string {
  if (file) return 'Reading the file…';
  return 'The bytes are held server-side, so there is nothing on this device to render. The report renders the document once the run finishes.';
}

export default function SubmitPage() {
  const toast = useToast();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { orgId } = useActiveOrg();
  const { user } = useAuth();

  // The submit page picks its organization from an inline list rather than
  // the header dropdown the other pages use: submitting is the one action
  // where the org is a required input, so the choice is part of the flow.
  // `choosing` re-opens the list after a pick ("Change" in the header).
  const [choosing, setChoosing] = useState(false);
  const memberships = user?.memberships ?? [];
  const activeOrg = memberships.find((m) => m.org_id === orgId);
  const needsOrg = !orgId || orgId === ALL_ORGS;
  const canSwitchOrg = memberships.length > 1 || !!user?.is_admin;

  // Tab and search term live in the URL rather than in state: a search is
  // something an analyst pastes to a colleague, and a reload in the middle of
  // one should land back on it rather than on an empty box.
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const tab: Tab = tabParam === 'search' ? tabParam : 'upload';
  const searchTerm = params.get('q') ?? '';

  const [file, setFile] = useState<File | null>(null);
  // Seeded from the URL so another page can hand this one a staged upload it
  // already created - the report's "Detonate" on a file a page downloaded
  // stages it there and lands here on the tree for it. Read once, on mount:
  // after that the flow below owns which upload is open.
  const [stagedId, setStagedId] = useState<string | null>(() => params.get('staged'));
  const [progress, setProgress] = useState<{ loaded: number; total: number } | null>(null);
  const [uploading, setUploading] = useState(false);
  // The URL retrieval form. Local to the page, never the URL bar: a draft is
  // not worth deep-linking, and the source URL must not leak into navigation
  // state or cache keys. The parse error is held apart from the mutation's so
  // a client-side refusal and a server-side one can clear independently.
  const [urlDraft, setUrlDraft] = useState('');
  const [urlInvalid, setUrlInvalid] = useState<string | null>(null);
  // Ownership of the one staged slot. Every adoption and every new acquisition
  // bumps it; a retrieval that recorded an older number has been superseded and
  // its result must not overwrite the winner. A ref, because the guard has to
  // survive renders: it is read from callbacks that may predate the bump.
  const acquisitionSeq = useRef(0);

  // null means the analyst has not touched the selection yet, which is NOT the
  // same as having chosen nothing. Collapsing the two makes the empty selection
  // unreachable: unticking the default would fall straight back to it.
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [password, setPassword] = useState(DEFAULT_ARCHIVE_PASSWORD);
  // A refused file type is shown beside the dropzone, not in a toast: the
  // message names the type and the fix is to drop a different file, so it has
  // to stay on screen next to where that happens.
  const [unsupported, setUnsupported] = useState<string | null>(null);
  const [timeoutSecs, setTimeoutSecs] = useState<number>(600);
  const [osFamily, setOsFamily] = useState<string>('windows11');
  const [startMode, setStartMode] = useState<StartMode>('auto');
  // Keyed by entry id, because a command line belongs to a FILE rather
  // than to the batch: one upload can hold an installer and a library.
  // Entries absent from it take the sandbox's defaults, which is every
  // entry on nearly every submission.
  const [launchOptions, setLaunchOptions] = useState<Record<string, LaunchOption>>({});
  // The analyst's exit country, null while they leave the deployment default.
  const [exitCountry, setExitCountry] = useState<string | null>(null);

  // The same catalog the URL console reads, for the countries a file
  // analysis may leave from. A failed read offers no picker, and the run
  // takes the default, which is what an untouched picker sends too.
  const catalogQ = useQuery({ queryKey: ANALYZER_CATALOG_KEY, queryFn: api.analyzerCatalog });
  const vmExitCountries = catalogQ.data?.vmExitCountries ?? [];

  const quotaQ = useQuery({
    queryKey: ['quota', orgId],
    queryFn: () => api.getQuota(orgId!),
    enabled: !!orgId && orgId !== ALL_ORGS,
    refetchInterval: 30_000,
  });

  // Read before the mutations below: a hard quota block has to reach the URL
  // form's guards, not only the banners and the Analyze button.
  const quota = quotaQ.data;
  const used = quota?.used ?? 0;
  const quotaTotal = quota?.usage_limit ?? 0;
  const atLimit = !!quota && quotaTotal > 0 && used >= quotaTotal;
  const overQuota = atLimit && quota?.overage_policy === 'block';
  const surcharging = atLimit && quota?.overage_policy === 'soft';

  // What the platform detonates. Static for the life of the deployment, so it
  // is fetched once and never refetched.
  const fileTypesQ = useQuery({
    queryKey: ['file-types'],
    queryFn: () => api.getFileTypes(),
    staleTime: Number.POSITIVE_INFINITY,
  });

  // How much of the fleet can start an analysis immediately. Polled on the
  // fleet-page interval because warmth moves on its own — machines warm up and
  // are spent continuously — and the figure is only worth showing while it is
  // roughly current. Failure is silent by design: the options panel omits the
  // line rather than reporting an error for something the form does not need.
  const systemsQ = useQuery({
    queryKey: ['systems'],
    queryFn: () => api.listSystemImages(),
    refetchInterval: 10_000,
  });

  // Warm machines behind the OS the analyst actually picked. A fleet-wide total
  // would promise warmth their own choice cannot reach: a warm Windows 11
  // machine does nothing for a Windows 10 submission. Undefined until the first
  // answer lands, which is what keeps the hint absent rather than showing zero.
  const warmForOS = useMemo(() => {
    const images = systemsQ.data;
    if (!images) return undefined;
    return images
      .filter((img) => img.os_family === osFamily)
      .reduce((n, img) => n + img.warm_machines, 0);
  }, [systemsQ.data, osFamily]);

  const stagedQ = useQuery({
    queryKey: ['staged', stagedId],
    queryFn: () => api.getStagedUpload(stagedId!),
    enabled: !!stagedId,
    // Poll only while the server has work to do. A finished inspection stops
    // the timer rather than leaving the page talking to itself.
    //
    // The error check is not redundant: TanStack keeps the last successful data
    // on a failed refetch, so an upload seen pending once and then erroring for
    // good would keep this firing at 1.5s forever, with the spinner rendered
    // next to the "no longer available" banner.
    refetchInterval: (q) =>
      q.state.status !== 'error' && q.state.data && PENDING_STATES.has(q.state.data.status)
        ? INSPECTION_POLL_MS
        : false,
  });

  const staged = stagedQ.data;

  // What this org has already run on these bytes, answered off the digest the
  // SERVER computed for the staged upload rather than one hashed here. The
  // page previously hashed the dropped file itself, which answered only while
  // the upload was still in flight: the moment staging finished the whole
  // branch holding the card unmounted, so the answer vanished exactly when the
  // analyst reached the button that spends a run on it. The staged digest is
  // also the only source that covers the other two ways a sample gets here —
  // adopted from an identifier search, and staged from a prior run's bytes.
  const priorQ = useQuery({
    queryKey: ['prior-sample', orgId, staged?.sample_sha256],
    queryFn: () => api.priorSampleAnalyses(orgId!, staged!.sample_sha256),
    enabled: !!orgId && orgId !== ALL_ORGS && !!staged?.sample_sha256,
    staleTime: 30_000,
  });
  // Empty for none and for not-yet-known, which the card renders identically —
  // it says nothing at all until there is something to say. Shown, never
  // enforced: every run is kept, and re-running is how a stale verdict is
  // refreshed.
  const priorRuns = priorQ.data ?? [];

  /** Bytes for the preview, held only for the file the analyst uploaded:
   *  anything inside an archive was walked, hashed and discarded server-side.
   *  Read from the File the browser already has rather than fetched back. */
  const [previewBytes, setPreviewBytes] = useState<Uint8Array | null>(null);

  useEffect(() => {
    if (!file) {
      setPreviewBytes(null);
      return;
    }
    let live = true;
    file
      .arrayBuffer()
      .then((buf) => {
        // A late read must not overwrite a newer file's bytes.
        if (live) setPreviewBytes(new Uint8Array(buf));
      })
      .catch(() => {
        // The preview already renders a "nothing to show" state, and a file the
        // browser cannot re-read is not worth interrupting the submit flow for.
        if (live) setPreviewBytes(null);
      });
    return () => {
      live = false;
    };
  }, [file]);

  /** Resolves an entry id to its display name for the link list. */
  const entryNameOf = useCallback(
    (entryId: string) => staged?.entries.find((e) => e.id === entryId)?.name ?? 'this sample',
    [staged]
  );
  const inspecting = !stagedQ.isError && !!staged && UNREADABLE_STATES.has(staged.status);
  /** The references are being retrieved. The tree is readable meanwhile. */
  const following = !stagedQ.isError && staged?.status === 'following';

  // Retrieving a sample from a URL. Owned by the page rather than the form so
  // the dropzone can be disabled against it (one retrieval at a time, and no
  // upload racing a fetch for the same staged slot), and so the adoption can
  // be guarded on the org it was started under: a switch mid-retrieval would
  // otherwise land one org's sample in another org's flow.
  //
  // The guard reads a ref, not the closure: a mutation's callbacks hold the
  // options from when mutate was called, so comparing against that closure's
  // orgId would always pass. The ref is what the page is under NOW.
  const orgIdRef = useRef(orgId);
  useEffect(() => {
    orgIdRef.current = orgId;
  }, [orgId]);

  /** Mints the slot ticket a new acquisition holds from the moment it starts.
   *  Minting bumps the counter, which is what supersedes every older
   *  in-flight acquisition: only the newest one may land. */
  const acquireSlot = useCallback(() => ++acquisitionSeq.current, []);

  /** The landing every way in shares. An acquisition owns the slot only if
   *  the org still matches and nothing newer has started; the checks read
   *  refs, not closures, because a mutation's callbacks hold the options
   *  from when mutate was called. */
  function adoptIfCurrent(upload: StagedUpload, acquisition: { slot: number; orgId: string }) {
    if (acquisition.orgId !== orgIdRef.current) {
      toast.info('The sample was fetched, but you had switched organizations. Fetch it again under this one.');
      return;
    }
    if (acquisition.slot !== acquisitionSeq.current) {
      toast.info('The sample was fetched, but another sample was opened first.');
      return;
    }
    adoptStaged(upload);
  }

  const urlM = useMutation({
    mutationFn: (v: { orgId: string; url: string; slot: number }) =>
      api.stageURLSubmission(v.orgId, v.url),
    onSuccess: (upload, v) => adoptIfCurrent(upload, { slot: v.slot, orgId: v.orgId }),
    // Rendered inline in the form, beside the field it names a problem with -
    // a toast would be gone before a slow retrieval's error could be read.
  });

  /** Clears the URL form and any retrieval attempt left in it. */
  function resetUrlEntry() {
    setUrlDraft('');
    setUrlInvalid(null);
    urlM.reset();
  }

  function submitUrl(e: React.FormEvent) {
    e.preventDefault();
    // The over-quota check mirrors the backend's admission rule: a retrieval
    // it will refuse at the door is not offered here.
    if (!orgId || orgId === ALL_ORGS || urlM.isPending || uploading || overQuota) return;
    // Client-side check so an obvious mistake never pays for a round trip;
    // the server remains the authority on what it will and will not fetch.
    let parsed: URL;
    try {
      parsed = new URL(urlDraft.trim());
    } catch {
      setUrlInvalid('Enter a full URL, starting with http:// or https://');
      return;
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      setUrlInvalid('Only http:// and https:// URLs can be retrieved');
      return;
    }
    setUrlInvalid(null);
    urlM.mutate({ orgId, url: parsed.toString(), slot: acquireSlot() });
  }

  const onDrop = useCallback(
    (accepted: File[]) => {
      const dropped = accepted[0];
      if (!dropped || !orgId || orgId === 'all') return;
      // A file-picker or drag extraction can finish after the dropzone's
      // disabled prop has already flipped for a pending retrieval, so the
      // guard lives here and not only in the prop.
      if (urlM.isPending) {
        toast.error('Wait for the URL retrieval to finish first.');
        return;
      }
      if (quotaQ.data && dropped.size > quotaQ.data.max_sample_size_bytes) {
        toast.error(`File exceeds your max sample size (${formatBytes(quotaQ.data.max_sample_size_bytes)})`);
        return;
      }

      setFile(dropped);
      setSelected(null);
      setUnsupported(null);
      setUploading(true);
      setProgress({ loaded: 0, total: dropped.size });
      // Same slot-ownership rule every way in shares: minting the ticket
      // supersedes older in-flight work, and an adoption that lands while
      // this upload runs must not overwrite it in return.
      const mySlot = acquireSlot();
      const startOrg = orgId;

      api
        .stageSubmission(orgId, {
          file: dropped,
          onProgress: (loaded, total) => setProgress({ loaded, total }),
        })
        .then((upload) => {
          if (startOrg !== orgIdRef.current) {
            toast.info('The upload finished, but you had switched organizations. Upload it again under this one.');
            return;
          }
          if (mySlot !== acquisitionSeq.current) {
            toast.info('The upload finished, but another sample was opened first.');
            return;
          }
          setStagedId(upload.id);
          qc.setQueryData<StagedUpload>(['staged', upload.id], upload);
        })
        .catch((e: unknown) => {
          if (e instanceof ApiError && e.kind === 'unsupported_media') {
            setUnsupported(e.message);
          } else {
            toast.error(e instanceof ApiError ? e.message : 'Upload failed');
          }
          setFile(null);
        })
        .finally(() => {
          setUploading(false);
          setProgress(null);
        });
    },
    // urlM: the pending-retrieval guard above must see the live mutation
    // state, not the snapshot from whenever this callback was memoized.
    [orgId, qc, quotaQ.data, toast, urlM]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    maxFiles: 1,
    // A retrieval holds the same staged slot an upload would, so the two
    // cannot run at once: the loser would either overwrite the other's tree
    // or strand the winner's result.
    disabled: uploading || urlM.isPending || !!stagedId,
  });

  // The server names the entry to start with: the program inside the upload,
  // or the upload itself when nothing in it can be started. It classifies from
  // file headers, which the page cannot do, so the choice belongs there.
  const defaultPick = useMemo(
    () => staged?.entries.find((e) => e.recommended)?.id,
    [staged]
  );
  const effectiveSelection = useMemo(
    () => selected ?? new Set(defaultPick ? [defaultPick] : []),
    [selected, defaultPick]
  );

  /** The entry the preview shows: the analyst's single pick, or the upload
   *  itself. A multi-pick selection has no one document to preview. */
  const previewEntry = useMemo(() => {
    if (!staged) return null;
    const root = staged.entries.find((e) => e.origin === 'root') ?? null;
    if (effectiveSelection.size !== 1) return root;
    const only = [...effectiveSelection][0];
    return staged.entries.find((e) => e.id === only) ?? root;
  }, [staged, effectiveSelection]);

  function toggleEntry(id: string) {
    // The first toggle starts from whatever was shown - the implicit default -
    // so clicking the pre-ticked root entry unticks it rather than re-adding it.
    setSelected((prev) => {
      const next = new Set(prev ?? effectiveSelection);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAll() {
    if (!staged) return;
    const pickable = staged.entries.filter((e) => e.selectable).slice(0, staged.max_picks);
    setSelected(new Set(pickable.map((e) => e.id)));
    if (staged.entries.filter((e) => e.selectable).length > staged.max_picks) {
      toast.error(`Selected the first ${staged.max_picks}; that is the per-upload limit.`);
    }
  }

  // Both writes go through TanStack rather than hand-rolled busy flags: it
  // already owns the cache these invalidate, and isPending is one source of
  // truth for the button state instead of a boolean that can drift from it.
  const passwordM = useMutation({
    mutationFn: (secret: string) => api.setStagedPassword(stagedId!, secret),
    onSuccess: async (updated) => {
      qc.setQueryData<StagedUpload>(['staged', stagedId!], updated);
      await stagedQ.refetch();
    },
    onError: (e: Error) =>
      toast.error(e instanceof ApiError ? e.message : 'Could not apply the password'),
  });

  // No bare_metal: the dashboard submits on the virtual-machine lane only, and
  // the options panel says so. The client defaults the field to false, and the
  // /v1 API still carries it for a caller that has the lane provisioned.
  // Only the picked entries whose launch is configurable, which is what
  // decides whether the panel appears at all.
  const launchTargetList = useMemo(
    () => launchTargets(stagedQ.data?.entries ?? [], selected),
    [stagedQ.data?.entries, selected]
  );

  const analyzeM = useMutation({
    mutationFn: (picks: string[]) =>
      api.submitStagedUpload(stagedId!, {
        entries: picks,
        osFamily,
        platform: 'windows',
        timeoutSecs,
        startMode,
        // Narrowed to the entries this submit detonates. The panel only ever
        // offers picked entries, but a selection changed after configuring one
        // would otherwise send a key for an entry left behind, which the server
        // refuses outright rather than ignoring.
        launchOptions: Object.fromEntries(
          Object.entries(launchOptions).filter(([id]) => picks.includes(id))
        ),
        exitCountry: currentExitCountry(exitCountry, vmExitCountries) ?? undefined,
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['submissions', orgId] });
      qc.invalidateQueries({ queryKey: ['quota', orgId] });
      // One submission opens its own report, because the live screen is there
      // and watching the sample run is the reason it was submitted. A batch has
      // no single report to open, so it still lands on the list.
      const ids = res.submissions.map((s) => s.submission_id).filter(Boolean);
      if (ids.length === 1) {
        nav(`/submissions/${ids[0]}`);
        return;
      }
      nav('/submissions', ids[0] ? { state: { newSubmissionId: ids[0] } } : undefined);
    },
    onError: (e: Error) =>
      toast.error(e instanceof ApiError ? e.message : 'Could not queue the analysis'),
  });

  function applyPassword() {
    if (!stagedId || !password) return;
    passwordM.mutate(password);
  }

  function analyze() {
    if (!stagedId || !staged) return;
    const picks = [...effectiveSelection];
    if (picks.length === 0) return;
    analyzeM.mutate(picks);
  }

  function startOver() {
    setFile(null);
    setStagedId(null);
    setSelected(null);
    setPassword(DEFAULT_ARCHIVE_PASSWORD);
    resetUrlEntry();
    // And out of the URL, or a reload resurrects the upload just abandoned:
    // `stagedId` is seeded from this parameter on mount, so leaving it behind
    // would make Start over hold only until the page was loaded again.
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        out.delete('staged');
        return out;
      },
      { replace: true },
    );
  }

  function goToTab(next: Tab) {
    // replace, not push: flipping between the two halves of one page is not a
    // step an analyst wants to walk back through with the Back button.
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        if (next === 'upload') out.delete('tab');
        else out.set('tab', next);
        return out;
      },
      { replace: true },
    );
  }

  function setSearchTerm(value: string) {
    setParams((prev) => {
      const out = new URLSearchParams(prev);
      out.set('tab', 'search');
      if (value === '') out.delete('q');
      else out.set('q', value);
      return out;
    });
  }

  /** A detonation from the search tab hands over a staged upload that is
   *  indistinguishable from a dropped file's, so the rest of this page takes it
   *  from here unchanged. */
  function adoptStaged(upload: StagedUpload) {
    // This sample owns the slot from here; a retrieval still in flight is
    // superseded and will be refused when it lands.
    acquisitionSeq.current += 1;
    setFile(null);
    setSelected(null);
    setPassword(DEFAULT_ARCHIVE_PASSWORD);
    resetUrlEntry();
    setStagedId(upload.id);
    qc.setQueryData<StagedUpload>(['staged', upload.id], upload);
    goToTab('upload');
    toast.success('Fetched the sample. Pick what to detonate.');
  }

  // A settled inspection where nothing is selectable: the upload staged fine
  // but no file inside it is a type the platform detonates. Without this the
  // page would show a tree of greyed rows and a disabled button with no reason
  // given.
  const nothingDetonable =
    !!staged && !inspecting && !stagedQ.isError && staged.entries.every((e) => !e.selectable);
  const rootReason = staged?.entries.find((e) => e.unavailable_reason)?.unavailable_reason;

  const pickCount = effectiveSelection.size;
  const blockedReason = analyzeBlockedReason({
    gone: stagedQ.isError,
    overQuota,
    inspecting,
    following,
    pickCount,
    maxPicks: staged?.max_picks ?? 0,
  });

  return (
    <>
      {/* OUTSIDE the narrow column below. The header spans the page like every
          other one; the active-org readout lands where the picker sits on the
          dashboard, so the same corner answers "as which org" everywhere. */}
      <div className="page-header">
        <div>
          <h1>Submit a sample</h1>
          <p className="text-muted">
            Upload a file, or search a hash, URL, IP address or domain. Either way you see what is
            inside before choosing what to detonate.
          </p>
        </div>
        {!needsOrg && activeOrg && (
          <div
            className="inline-flex items-center gap-2 text-sm text-ink-300 max-w-[20rem] min-w-0"
            title={activeOrg.org_name}
          >
            <Building2 className="w-4 h-4 text-ink-500 shrink-0" />
            <span className="truncate">{activeOrg.org_name}</span>
            {canSwitchOrg && !choosing && (
              <button
                type="button"
                onClick={() => setChoosing(true)}
                className="text-xs font-medium text-accent-400 hover:text-accent-300 transition-colors shrink-0"
              >
                Change
              </button>
            )}
          </div>
        )}
      </div>

      <div style={{ maxWidth: '900px', margin: '0 auto' }}>

      {needsOrg || choosing ? (
        <OrgChooser
          activeOrgId={needsOrg ? null : orgId}
          onPicked={() => setChoosing(false)}
          onCancel={needsOrg ? undefined : () => setChoosing(false)}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {overQuota && (
            <Banner tone="danger">
              <strong>Quota exceeded.</strong> You have used {used} of {quotaTotal}. New submissions are blocked.
            </Banner>
          )}
          {surcharging && (
            <Banner tone="warning">
              <strong>Over quota.</strong> You have used {used} of {quotaTotal}. Submissions continue but are flagged as over-limit.
            </Banner>
          )}

          {/* The tab strip disappears once an upload is staged. At that point
              the analyst is mid-flow on a specific sample, and offering to
              switch to the search box beside it invites abandoning work that
              is already paid for in an outbound fetch or an upload. */}
          {!stagedId && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Segmented
                ariaLabel="How to submit"
                onChange={goToTab}
                options={TABS}
                value={tab}
              />
            </div>
          )}

          {!stagedId && tab === 'search' && (
            <IntelSearchPanel
              acquireSlot={acquireSlot}
              onStaged={adoptIfCurrent}
              onTerm={setSearchTerm}
              orgId={orgId}
              term={searchTerm}
            />
          )}

          {!stagedId && tab === 'upload' && (
            <>
              {unsupported && (
                <Banner tone="danger">
                  <strong>This file cannot be analysed.</strong> {unsupported}
                  <div className="mt-2">
                    <SupportedTypesList data={fileTypesQ.data} />
                  </div>
                </Banner>
              )}
              <Dropzone
                rootProps={getRootProps()}
                inputProps={getInputProps()}
                isDragActive={isDragActive}
                file={file}
                uploading={uploading}
                progress={progress}
                fileTypes={fileTypesQ.data}
              />
              <UrlStagePanel
                url={urlDraft}
                onUrl={(v) => {
                  setUrlDraft(v);
                  if (urlInvalid) setUrlInvalid(null);
                  // A server refusal was about the URL that was sent; editing
                  // the draft retires it.
                  if (urlM.error) urlM.reset();
                }}
                onSubmit={submitUrl}
                pending={urlM.isPending}
                busyElsewhere={uploading}
                quotaBlocked={overQuota}
                error={
                  urlInvalid ??
                  (urlM.error instanceof ApiError
                    ? urlM.error.message
                    : urlM.error
                      ? 'The URL could not be retrieved.'
                      : null)
                }
              />
            </>
          )}

          {stagedId && (
            <>
              {/* These exact bytes have been analysed here before, above the
                  summary and the Start button rather than beside the dropzone.
                  The upload is the wrong place for it: the branch that held it
                  unmounts the instant staging finishes, so the answer was on
                  screen for the length of a transfer and gone by the time
                  anything could be decided with it. Never enforced — the
                  analyst may well be refreshing a verdict on something that has
                  changed, and every run is kept. No re-run control: the Start
                  button below this IS the re-run. */}
              {priorRuns.length > 0 && <AnalysedHere runs={priorRuns} />}
              <SummaryCard staged={staged} fallbackName={file?.name ?? ''} onStartOver={startOver} />

              {staged?.needs_password && (
                <PasswordPrompt
                  value={password}
                  busy={passwordM.isPending}
                  reason={staged.state_reason}
                  onChange={setPassword}
                  onApply={() => void applyPassword()}
                />
              )}

              {stagedQ.isError && (
                <Banner tone="danger">
                  <strong>This upload is no longer available.</strong>{' '}
                  {stagedQ.error instanceof ApiError
                    ? stagedQ.error.message
                    : 'It may have expired. Upload the sample again.'}
                </Banner>
              )}

              {staged?.status === 'inspect_failed' && (
                <Banner tone="danger">
                  <strong>Could not inspect this file.</strong>{' '}
                  {staged.state_reason || 'It can still be submitted as a whole file.'}
                </Banner>
              )}

              {staged?.entries_truncated && (
                <Banner tone="warning">
                  <strong>Partial listing.</strong> {staged.state_reason || 'This file contains more than the inspector will enumerate.'}
                </Banner>
              )}

              {nothingDetonable && (
                <Banner tone="danger">
                  <strong>Nothing here can be analysed.</strong>{' '}
                  {rootReason || 'No file in this upload is a type the platform can detonate.'}
                  <div className="mt-2">
                    <SupportedTypesList data={fileTypesQ.data} />
                  </div>
                </Banner>
              )}

              {/* The run's settings and the button that starts it sit ABOVE
                  the contents, not under them. What OS, for how long and on
                  which lane apply to every file in the tree, so they are the
                  frame the picking happens inside rather than a footnote an
                  analyst scrolls past a long archive to reach. */}
              <OptionsPanel
                osFamily={osFamily}
                onOsFamily={setOsFamily}
                warmForOS={warmForOS}
                startMode={startMode}
                onStartMode={setStartMode}
                timeoutSecs={timeoutSecs}
                onTimeout={setTimeoutSecs}
                exitCountries={vmExitCountries}
                defaultExitCountry={catalogQ.data?.defaultVmExitCountry ?? ''}
                exitCountry={exitCountry}
                onExitCountry={setExitCountry}
                disabled={analyzeM.isPending}
              />

              {/* A sibling of OptionsPanel rather than a section inside it: the
                  settings above describe the MACHINE and apply to every file in
                  the tree, which is what that panel promises. A command line
                  describes the file, so it is per entry. It renders nothing
                  unless a picked entry can take one. */}
              <LaunchOptionsPanel
                targets={launchTargetList}
                options={launchOptions}
                onChange={setLaunchOptions}
                disabled={analyzeM.isPending}
              />

              <div
                className="card"
                style={{
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '16px',
                  flexWrap: 'wrap',
                }}
              >
                <div className="text-muted text-sm">
                  {pickCount} of {staged?.max_picks ?? 0} selected
                  {blockedReason && <span style={{ marginLeft: '8px' }}>· {blockedReason}</span>}
                </div>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!!blockedReason || analyzeM.isPending}
                  onClick={() => void analyze()}
                >
                  {analyzeM.isPending ? (
                    <>
                      <Loader2 size={16} className="spin" /> Queueing…
                    </>
                  ) : (
                    `Analyze ${pickCount === 1 ? 'sample' : `${pickCount} samples`}`
                  )}
                </button>
              </div>

              {inspecting ? (
                <div className="card" style={{ padding: '48px 24px', textAlign: 'center' }}>
                  <Loader2 size={28} className="spin" style={{ margin: '0 auto 12px', color: 'var(--accent-blue)' }} />
                  <div style={{ fontWeight: 500 }}>Looking inside…</div>
                  <p className="text-muted text-sm mt-2">Unpacking archives and scanning for embedded payloads.</p>
                </div>
              ) : (
                staged && (
                  <>
                    <StagedFileTree
                      entries={staged.entries}
                      selected={effectiveSelection}
                      maxPicks={staged.max_picks}
                      onToggle={toggleEntry}
                      onSelectAll={selectAll}
                      onClear={() => setSelected(new Set())}
                      disabled={analyzeM.isPending}
                    />

                    {following && (
                      // The tree is readable and the payloads are still
                      // landing. Submitting now is refused by the server -
                      // a selection made against half a tree would consume the
                      // upload out from under the phase writing to it - so the
                      // page says so rather than letting the button fail.
                      <p className="rounded-lg border border-ink-800 bg-ink-900 px-4 py-2.5 text-prose text-ink-300">
                        Retrieving what this document points at. The tree below is complete for
                        the file itself; anything downloaded will appear beside it.
                      </p>
                    )}

                    {/* The question, above the evidence for it: what the
                        document downloaded is a decision the analyst makes,
                        and the reference list below is why. */}
                    <RetrievedPayloads
                      entries={staged.entries}
                      links={staged.links ?? []}
                      selected={effectiveSelection}
                      onToggle={toggleEntry}
                      disabled={analyzeM.isPending}
                    />

                    {/* Above the preview: a URL that fires on open is the
                        stronger finding, and the one acted on first. */}
                    <LinkList links={staged.links ?? []} entryName={entryNameOf} />

                    {/* Only the uploaded file's bytes are held client-side, so
                        a nested entry has nothing to render. A staged upload
                        the page did not receive as a File - one detonated
                        straight from the intel search - has none either. */}
                    {previewEntry && canPreview(previewKindOf(previewEntry)) && previewEntry.origin === 'root' && (
                      <PreviewPane
                        // Keyed so a different entry remounts: the pane holds
                        // render state that must not carry across documents.
                        key={previewEntry.id}
                        kind={previewKindOf(previewEntry)}
                        name={previewEntry.name}
                        bytes={previewEntry.origin === 'root' ? previewBytes : null}
                        unavailable={previewUnavailable(file)}
                      />
                    )}
                  </>
                )
              )}
            </>
          )}
        </div>
      )}
      </div>
    </>
  );
}

/** Why the Analyze button is disabled, in words. A disabled control with no
 *  stated reason is the single most common way a form becomes unusable. */
// analyzeBlockedReason names why Analyze is unavailable, or returns '' when it
// is not.
//
// A failed inspection is deliberately absent. It is not a block: the server
// records a root entry for the upload itself whichever way inspection ended, so
// the file stays submittable whole, and the banner above already says so.
// Blocking on it would leave an analyst reading "it can still be submitted"
// beside a button that refuses to.
function analyzeBlockedReason(s: {
  gone: boolean;
  overQuota: boolean;
  inspecting: boolean;
  following: boolean;
  pickCount: number;
  maxPicks: number;
}): string {
  if (s.gone) return 'this upload is no longer available';
  if (s.overQuota) return 'blocked while over quota';
  if (s.inspecting) return 'waiting for inspection to finish';
  // The server refuses a submit mid-follow: a selection made against half a
  // tree would consume the upload out from under the phase writing to it.
  if (s.following) return 'waiting for the downloads to finish';
  if (s.pickCount === 0) return 'select at least one file';
  if (s.pickCount > s.maxPicks) return `at most ${s.maxPicks} per upload`;
  return '';
}

function Banner({ tone, children }: { tone: 'danger' | 'warning'; children: React.ReactNode }) {
  return (
    <div
      className="card"
      style={{
        padding: '16px',
        display: 'flex',
        gap: '12px',
        background: `var(--status-${tone}-bg)`,
        borderColor: `var(--status-${tone})`,
      }}
    >
      <AlertTriangle style={{ color: `var(--status-${tone})`, flexShrink: 0 }} />
      <div>{children}</div>
    </div>
  );
}

/** The accepted-types catalogue as a label/value grid: one row per group, one
 *  for archives, every extension named. A flat sentence at this length reads
 *  as a wall; the left rail of labels is what makes it scannable. */
function SupportedTypesList({ data }: { data: SupportedFileTypes | undefined }) {
  const view = selectSupportedTypes(data);
  if (!view) return <div className="text-muted text-sm">{SUPPORTED_TYPES_FALLBACK}</div>;

  return (
    <div className="text-sm">
      <dl
        style={{
          display: 'inline-grid',
          gridTemplateColumns: 'auto 1fr',
          columnGap: '16px',
          rowGap: '4px',
          margin: 0,
          textAlign: 'left',
        }}
      >
        {view.rows.map((row) => (
          <div key={row.label} style={{ display: 'contents' }}>
            <dt className="text-muted" style={{ whiteSpace: 'nowrap' }}>
              {row.label}
            </dt>
            <dd style={{ margin: 0 }}>{row.items}</dd>
          </div>
        ))}
      </dl>
      {view.note && <div className="text-muted mt-2">{view.note}</div>}
    </div>
  );
}

function Dropzone(props: {
  rootProps: Record<string, unknown>;
  inputProps: Record<string, unknown>;
  isDragActive: boolean;
  file: File | null;
  uploading: boolean;
  progress: { loaded: number; total: number } | null;
  fileTypes: SupportedFileTypes | undefined;
}) {
  // No `accept` filter on the input, deliberately: samples are routinely
  // hash-named or mis-extensioned, and filtering the picker would hide
  // legitimate submissions. The server decides, from the bytes.
  const { rootProps, inputProps, isDragActive, file, uploading, progress, fileTypes } = props;
  const pct = progress && progress.total > 0 ? Math.round((progress.loaded / progress.total) * 100) : 0;

  return (
    <div className="card" style={{ padding: '8px' }}>
      <div
        {...rootProps}
        style={{
          border: '2px dashed',
          borderColor: isDragActive ? 'var(--accent-blue)' : 'var(--border-strong)',
          backgroundColor: isDragActive ? 'var(--status-info-bg)' : 'var(--bg-app)',
          borderRadius: '8px',
          padding: '48px 24px',
          textAlign: 'center',
          cursor: uploading ? 'progress' : 'pointer',
          transition: 'var(--transition)',
        }}
      >
        <input {...inputProps} />
        {uploading && file ? (
          <div>
            <Loader2 size={32} className="spin" style={{ margin: '0 auto 16px', color: 'var(--accent-blue)' }} />
            <div style={{ fontWeight: 500 }}>Uploading {file.name}</div>
            <div className="text-muted text-sm mt-2">
              {formatBytes(progress?.loaded ?? 0)} of {formatBytes(file.size)} · {pct}%
            </div>
          </div>
        ) : (
          <div style={{ maxWidth: HERO_WIDTH, margin: '0 auto' }}>
            <SubmitFileIcon className="mx-auto" size={80} />
            <div className="mt-4" style={{ fontWeight: 500, fontSize: '1.125rem' }}>
              {isDragActive ? 'Drop to upload' : 'Drag a sample here, or click to browse'}
            </div>
            <div className="mt-4">
              <SupportedTypesList data={fileTypes} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** The URL retrieval form, under the dropzone in the upload tab.
 *
 *  Retrieval fetches the response bytes and stages them like an upload - the
 *  note points at /submit-url for the live browser lane so the two features
 *  are not confused. One staged slot serves every entry path, so a pending
 *  retrieval, a file upload in flight and a hard quota block each disable the
 *  form rather than race it.
 *
 *  The copy says "a file at a direct link", not "a website or file URL", and
 *  that is the point of it: this lane fetches BYTES and analyses them as a
 *  sample, so a reader who pastes a page they mean to have visited would get a
 *  file scan of an HTML response. A URL to open in a browser is the other
 *  lane's, and the sentence names it. */
function UrlStagePanel(props: {
  url: string;
  onUrl: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  pending: boolean;
  busyElsewhere: boolean;
  quotaBlocked: boolean;
  error: string | null;
}) {
  const { url, onUrl, onSubmit, pending, busyElsewhere, quotaBlocked, error } = props;
  const disabled = pending || busyElsewhere || quotaBlocked;
  return (
    <div className="card p-4">
      <form noValidate onSubmit={onSubmit}>
        <h2 className="font-medium">Analyse a file at a direct link</h2>
        <p className="text-muted mt-1 mb-3 text-sm">
          Paste a direct download URL and we will retrieve the file it points at and scan
          it, like an upload. It downloads the file rather than opening the page: to visit
          a site in a watched browser, use <Link to="/submit-url">Submit a URL</Link>.
        </p>
        <div className="flex gap-2">
          <input
            aria-label="URL to retrieve and analyse"
            aria-invalid={error !== null}
            autoComplete="off"
            className="input min-w-0 flex-1"
            disabled={disabled}
            onChange={(e) => onUrl(e.target.value)}
            placeholder="https://example.com/sample.exe"
            spellCheck={false}
            type="url"
            value={url}
          />
          <button className="btn-primary" disabled={disabled || url.trim() === ''} type="submit">
            {pending ? <Loader2 className="spin" size={16} /> : <Link2 size={16} />}
            Retrieve
          </button>
        </div>
        {pending && (
          <p className="text-muted mt-2 text-sm">
            Retrieving… the server is fetching the response and inspecting what arrived.
          </p>
        )}
        {quotaBlocked && !pending && (
          <p className="text-muted mt-2 text-sm">Retrieval is blocked while over quota.</p>
        )}
        {error !== null && (
          <p className="mt-2 text-sm" role="alert" style={{ color: 'var(--status-danger)' }}>
            {error}
          </p>
        )}
      </form>
    </div>
  );
}

function SummaryCard({
  staged,
  fallbackName,
  onStartOver,
}: {
  staged: StagedUpload | undefined;
  fallbackName: string;
  onStartOver: () => void;
}) {
  // Deliberately no score and no verdict: nothing has run yet, and a number
  // here would be read as one.
  const rows: Array<[string, React.ReactNode]> = [
    ['Target', staged?.target_name || fallbackName],
    ['Size', staged ? formatBytes(staged.sample_size) : '-'],
    ['Format', staged?.detected_format || 'unrecognised'],
    ['Submitted', staged ? new Date(staged.submitted_at).toLocaleString() : '-'],
    ['Password', staged?.has_password ? 'supplied' : 'none'],
  ];

  return (
    <div className="card" style={{ padding: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: '14px', minWidth: 0 }}>
          <FileWarning size={28} style={{ color: 'var(--accent-blue)', flexShrink: 0 }} />
          <dl
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              columnGap: '16px',
              rowGap: '4px',
              margin: 0,
              minWidth: 0,
            }}
          >
            {rows.map(([label, value]) => (
              <div key={label} style={{ display: 'contents' }}>
                <dt className="text-muted text-sm">{label}</dt>
                <dd style={{ margin: 0, wordBreak: 'break-all' }}>{value}</dd>
              </div>
            ))}
          </dl>
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onStartOver}>
          Replace file
        </button>
      </div>
    </div>
  );
}

function PasswordPrompt({
  value,
  busy,
  reason,
  onChange,
  onApply,
}: {
  value: string;
  busy: boolean;
  reason?: string;
  onChange: (v: string) => void;
  onApply: () => void;
}) {
  return (
    <div className="card" style={{ padding: '16px', borderColor: 'var(--status-warning)' }}>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
        <Lock size={20} style={{ color: 'var(--status-warning)', flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <strong>This archive is password protected.</strong>
          <p className="text-muted text-sm mt-1">
            {reason || 'Supply the password to list and detonate what is inside.'} Most samples distributed by mail use{' '}
            <code>infected</code>, which is filled in below.
          </p>
          <form
            style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}
            onSubmit={(e) => {
              e.preventDefault();
              onApply();
            }}
          >
            <input
              type="password"
              className="input"
              style={{ flex: 1, minWidth: '200px' }}
              value={value}
              autoComplete="off"
              aria-label="Archive password"
              onChange={(e) => onChange(e.target.value)}
            />
            <button type="submit" className="btn btn-secondary" disabled={busy || !value}>
              {busy ? <Loader2 size={16} className="spin" /> : 'Unlock'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

/** How the run is configured: which guest, for how long, where its traffic
 *  leaves from, on which lane.
 *
 *  One ruled group per question, so they read as a settings sheet rather than
 *  as loose controls stacked in a box. */
function OptionsPanel(props: {
  osFamily: string;
  onOsFamily: (v: string) => void;
  /** Machines warm for the SELECTED OS. Undefined until the fleet answers. */
  warmForOS: number | undefined;
  startMode: StartMode;
  onStartMode: (v: StartMode) => void;
  timeoutSecs: number;
  onTimeout: (v: number) => void;
  /** The countries a file analysis may leave from. Empty draws no picker. */
  exitCountries: readonly string[];
  defaultExitCountry: string;
  exitCountry: string | null;
  onExitCountry: (code: string | null) => void;
  disabled: boolean;
}) {
  const { osFamily, onOsFamily, warmForOS, startMode, onStartMode, timeoutSecs, onTimeout, disabled } = props;
  const { exitCountries, defaultExitCountry, exitCountry, onExitCountry } = props;
  // The chosen OS in the words the chips use, so the hint names what the
  // analyst picked rather than the wire value behind it.
  const osLabel = OS_CHOICES.find((o) => o.value === osFamily)?.label ?? osFamily;
  const minutes = Math.round(timeoutSecs / 60);

  return (
    <div className="card divide-y divide-rule">
      <OptionGroup icon={Server} label="Operating system">
        <div className="flex flex-wrap gap-2">
          {OS_CHOICES.map((os) => (
            <button
              key={os.value}
              type="button"
              aria-pressed={osFamily === os.value}
              disabled={disabled}
              onClick={() => onOsFamily(os.value)}
              className={cn(
                'ring-focus',
                CHOICE_BOX,
                choiceTone(osFamily === os.value ? 'active' : 'idle'),
                disabled ? 'cursor-not-allowed opacity-50' : 'hover:border-ink-600',
              )}
            >
              <OSIcon family={os.value} compact />
              {os.label}
            </button>
          ))}
        </div>
      </OptionGroup>

      {/* Directly under the OS, because its hint is about the machines behind
          the OS just chosen. Most analysts never touch it: the platform already
          prefers a machine that can start immediately, which is what Automatic
          says, and the other two exist for the analyst who has a reason. */}
      <OptionGroup icon={Rabbit} label="Start">
        <div className="flex flex-wrap gap-2">
          {START_CHOICES.map((choice) => (
            <button
              key={choice.value}
              type="button"
              aria-pressed={startMode === choice.value}
              disabled={disabled}
              onClick={() => onStartMode(choice.value)}
              className={cn(
                'ring-focus',
                CHOICE_BOX,
                choiceTone(startMode === choice.value ? 'active' : 'idle'),
                disabled ? 'cursor-not-allowed opacity-50' : 'hover:border-ink-600',
              )}
            >
              {choice.label}
            </button>
          ))}
        </div>
        <StartHint mode={startMode} warm={warmForOS} osLabel={osLabel} />
      </OptionGroup>

      <OptionGroup icon={Clock} label="Timeout">
        <div className="flex items-center gap-4">
          <input
            type="range"
            min={TIMEOUT_MIN_MINUTES}
            max={TIMEOUT_MAX_MINUTES}
            step={1}
            value={minutes}
            disabled={disabled}
            aria-label="Analysis timeout in minutes"
            onChange={(e) => onTimeout(Number(e.target.value) * 60)}
            className="min-w-0 flex-1"
            style={{ accentColor: 'var(--accent-blue)' }}
          />
          <output className="w-16 text-right text-prose tabular-nums text-ink-200">{minutes} min</output>
        </div>
      </OptionGroup>

      {exitCountries.length > 0 && (
        <OptionGroup
          icon={Globe}
          label="Exit country"
          hint={exitCountryHint(defaultExitCountry)}
        >
          <div className="max-w-xs">
            <ExitCountrySelect
              countries={exitCountries}
              defaultCountry={defaultExitCountry}
              value={exitCountry}
              onChange={onExitCountry}
              disabled={disabled}
            />
          </div>
        </OptionGroup>
      )}

      {/* Both lanes are shown and neither is a control: the dashboard runs
          every analysis in a virtual machine, so the group states where the
          sample goes and why the other option is not open, instead of leaving
          an analyst who knows bare metal exists wondering where it went. */}
      <OptionGroup
        icon={Cpu}
        label="Lane"
        hint="Bare metal is not available in the dashboard yet. Every analysis runs in a virtual machine."
      >
        <div className="flex flex-wrap gap-2">
          <span className={cn(CHOICE_BOX, choiceTone('active'))}>Virtual machine</span>
          <span className={cn(CHOICE_BOX, choiceTone('unavailable'))}>
            Bare metal
            <span className="rounded-chip border border-rule px-1.5 py-0.5 text-mini font-normal">
              Unavailable
            </span>
          </span>
        </div>
      </OptionGroup>
    </div>
  );
}

/** What the chosen start mode will actually do, given the fleet right now.
 *
 *  It states a CONSEQUENCE rather than a mechanism: an analyst choosing here is
 *  deciding how soon the run begins and whether the guest has been sitting
 *  idle, not configuring a pool. The live count is part of that consequence, so
 *  it lives with the choice rather than beside the operating system.
 *
 *  Three deliberate decisions, each the answer to a mistake the obvious design
 *  makes:
 *
 *  - No chip is ever disabled. `warm` has a defined fallback, so picking it
 *    with nothing warm is still meaningful — and a control that enables and
 *    disables itself on a ten-second poll flickers, which reads as a defect.
 *    The hint carries the truth instead.
 *  - Nothing is signalled by colour alone. The dot reinforces; the count and
 *    the consequence are text.
 *  - A capacity read that has not landed, or failed, degrades to the static
 *    sentence rather than a skeleton or an error. Every mode still says what it
 *    does; the count is the only part that needs the fleet. */
function StartHint({
  mode,
  warm,
  osLabel,
}: {
  mode: StartMode;
  warm: number | undefined;
  osLabel: string;
}) {
  if (mode === 'cold') {
    return (
      <p className="mt-2.5 text-mini text-ink-500">Always boots the machine, takes longer.</p>
    );
  }
  if (warm === undefined) {
    return (
      <p className="mt-2.5 text-mini text-ink-500">
        {mode === 'warm'
          ? 'Waits up to a minute for a machine that can start immediately, then boots a fresh one.'
          : 'Uses a machine that can start immediately when one is free.'}
      </p>
    );
  }
  if (warm === 0) {
    return (
      <p className="mt-2.5 flex items-center gap-1.5 text-mini text-ink-500">
        <span className="h-1.5 w-1.5 rounded-full bg-current" />
        {mode === 'warm'
          ? `No ${osLabel} machine warm right now. Waits up to a minute, then boots a fresh one.`
          : `No ${osLabel} machine warm right now. The first start takes about 25 s.`}
      </p>
    );
  }
  return (
    <p className="mt-2.5 flex items-center gap-1.5 text-mini text-ink-400">
      <span className="h-1.5 w-1.5 rounded-full bg-accent-400 animate-pulse-subtle" />
      <span className="tabular-nums text-accent-200">{warm}</span>
      {warm === 1 ? 'machine' : 'machines'} warm for {osLabel}. An analysis starts in seconds.
    </p>
  );
}

/** What the exit country decides, and where a busy pick falls back to, so the
 *  hint promises no more than the platform does. */
function exitCountryHint(defaultCountry: string): string {
  const fallback = defaultCountry !== '' ? `${countryName(defaultCountry)}, then to normal networking` : 'normal networking';
  return `Traffic from the sample leaves from this country. If it is busy, the run falls back to ${fallback}.`;
}

/** One labelled question in the options sheet. */
function OptionGroup({
  icon: Icon,
  label,
  hint,
  children,
}: {
  icon: LucideIcon;
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="px-4 py-4">
      <div className="mb-3 flex items-center gap-2">
        <Icon size={16} className="text-ink-500" />
        <h3 className="text-prose font-medium text-ink-100">{label}</h3>
      </div>
      {children}
      {hint && <p className="mt-2.5 text-mini text-ink-500">{hint}</p>}
    </section>
  );
}

/** The box every option is drawn in - a button where the analyst can change
 *  the answer, a plain chip where it is fixed. Shared so the two never drift
 *  into looking like different kinds of thing. */
const CHOICE_BOX =
  'inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-prose font-medium transition-colors';

function choiceTone(state: 'active' | 'idle' | 'unavailable'): string {
  switch (state) {
    case 'active':
      return 'border-accent-500 bg-accent-500/10 text-accent-100';
    case 'idle':
      return 'border-rule-strong bg-ink-950/40 text-ink-300';
    case 'unavailable':
      return 'border-dashed border-rule bg-transparent text-ink-600';
  }
}

/** The organizations the analyst can submit under, as a pickable list.
 *
 *  Stands where the upload UI will: a submission is always org-scoped, so
 *  until an org is chosen the choice IS the page. Creation is a platform-admin
 *  capability (the modal on /admin/orgs), so only admins get the create link;
 *  everyone else with no memberships is told who can fix that. */
function OrgChooser({
  activeOrgId,
  onPicked,
  onCancel,
}: {
  activeOrgId: string | null;
  onPicked: () => void;
  onCancel?: () => void;
}) {
  const { user } = useAuth();
  const { pick } = useActiveOrg();
  const memberships = user?.memberships ?? [];
  const isAdmin = !!user?.is_admin;

  if (memberships.length === 0) {
    return (
      <div className="card mx-auto w-full max-w-md px-8 py-12 text-center">
        <AlertTriangle size={32} className="mx-auto mb-4 text-ink-500" />
        <h2>No organization yet</h2>
        <p className="text-muted mt-2">
          {isAdmin
            ? 'Samples are submitted under an organization, and none exists yet.'
            : "You aren't a member of any organization. Ask your admin to invite you."}
        </p>
        {isAdmin && (
          <Link to="/admin/orgs" className="btn-primary mt-6 inline-flex">
            Create an organization
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="card mx-auto w-full max-w-md p-6">
      <h2>Select an organization</h2>
      <p className="text-muted mt-1 text-sm">
        The submission runs under the organization you pick and counts against its quota.
      </p>
      <div className="mt-4 flex flex-col gap-1">
        {memberships.map((m) => (
          <button
            key={m.org_id}
            type="button"
            onClick={() => {
              pick(m.org_id);
              onPicked();
            }}
            title={m.org_name}
            className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm rounded-md border border-ink-800 hover:bg-ink-800/60 text-left transition-colors"
          >
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
              <Building2 className="w-4 h-4 text-ink-500 shrink-0" />
              <div className="min-w-0">
                <div className="text-ink-100 font-medium truncate">{m.org_name}</div>
                <div className="text-xs text-ink-500 truncate">
                  {m.role === 'org_admin' || m.role === 'manager' ? 'Management' : 'Member'}
                </div>
              </div>
            </div>
            {m.org_id === activeOrgId && <Check className="w-4 h-4 text-accent-400 shrink-0" />}
          </button>
        ))}
      </div>
      {(isAdmin || onCancel) && (
        <div className="mt-4 flex items-center justify-between border-t border-ink-800 pt-3 text-sm">
          {isAdmin ? (
            <Link to="/admin/orgs" className="text-accent-400 hover:text-accent-300 transition-colors">
              Create an organization
            </Link>
          ) : (
            <span />
          )}
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="text-ink-400 hover:text-ink-200 transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      )}
    </div>
  );
}
